/**
 * The nutrition label shown on the Edit Food page: which nutrient rows appear, in which
 * order, what the header above the table says, and the arithmetic that turns a food's
 * stored nutrient values into the numbers in those rows.
 *
 * Everything here is a plain function taking what it needs as arguments. Nothing in this
 * module knows about the page, Angular, or any service — which is the whole point, because
 * the arithmetic below is the part most likely to be got wrong and it could not be tested
 * without a browser while it lived on the component. See `nutrition-label.test.ts`.
 *
 * The page keeps every field it had. It calls these functions and assigns the results, so
 * the save path (`save`, `validity`, `updateAsync` in `edit-food.ts`) still reads exactly
 * the same fields it always did. That is deliberate: the food's edited nutrient values are
 * both this label's working state and the save path's input, so giving this module
 * ownership of them would mean the two had to agree on who converts between what the user
 * sees and what is stored. Leaving the fields on the page means that question never arises.
 *
 * Two of these functions CHANGE THE OBJECT THEY ARE GIVEN rather than returning a fresh
 * one, and that is not an oversight: the page's template binds two-way into those objects,
 * so replacing them would break the bindings without any visible error. Each says so.
 *
 * Follows `recipe-measures.ts` and `food-source-display.ts`, which were peeled off this
 * same page the same way.
 */

import { INFOODS, Food, Nutrients, FoodMeasure, LABEL_SERVING_MEASURE_NAME, isNutrientValueEntered } from '@cf-shared/data-model/food';
import { Recipe, isRecipe, RECIPE_SERVINGS_MEASURE_NAME } from '@cf-shared/data-model/recipe';
import { LocaleSettings } from '@cf-shared/shared-util';
import { formatMeasure } from '@cf-shared/measure-format';
import { NutritionLabelStyle } from '@cf-shared/enums';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { LL } from 'i18n/i18n.service';
import { formatServingSize, translateMeasureName } from 'util/formatting';
import type { TranslationFunctions } from '../../../gensrc/i18n/i18n-types';

/**
 * Which set of values the label is showing: the amount in one serving, or the amount in
 * 100 grams.
 *
 * A string union rather than an enum because the page's segment control binds these exact
 * two strings from the template (`[value]="'serving'"`), so turning them into enum values
 * would change the template and the stored setting alongside a move that is meant to
 * change no behavior at all.
 */
export type MacrosDisplayMode = 'serving' | '100g';

/** One row of the nutrition label. */
export interface NutritionLabelEntry {
  tag: string; // INFOODS tag for the nutrient
  label?: string; // label only shown in the food creation form
  indent?: number; // indent by N levels underneath its "parents"
  bold?: true;
  displayNutrient?: UiDisplayNutrient; // reference to the actual value in the main table
}

/** A whole label: which style it follows, and its rows in the order they are shown. */
export interface NutritionLabelOrder {
  style: NutritionLabelStyle;
  entries: NutritionLabelEntry[];
}

/**
 * What the carbohydrate reconciliation below decided.
 *
 * `carbNet` and `carbTot` are present only when that value should be written onto the
 * food's edited nutrients; absent means leave what is there alone. `totalCarbsOrg_g` is
 * the remembered original total to carry forward, and stays `undefined` while no value has
 * been valid yet.
 */
export interface TotalCarbsReconciliation {
  carbNet?: number;
  carbTot?: number;
  totalCarbsOrg_g: number | undefined;
}

/**
 * Builds the label's rows for one label style, with their names in the reader's language.
 *
 * The two tables are written out rather than derived, because the order and the indenting
 * are what the two label formats legally require, not something computed from the data.
 */
export function buildNutritionLabelOrder(
  style: NutritionLabelStyle,
  ll: TranslationFunctions = LL,
): NutritionLabelOrder {
  if (style === NutritionLabelStyle.US) {
    return {
      style: NutritionLabelStyle.US,
      entries: [
        { tag: INFOODS.Calories, label: ll.editFood.nutrients.calories(), bold: true },
        { tag: INFOODS.Fat, label: ll.editFood.nutrients.totalFat(), bold: true },
        { tag: 'FASAT_g', label: ll.editFood.nutrients.saturatedFat(), indent: 1 },
        { tag: 'FATRN_g', label: ll.editFood.nutrients.transFat(), indent: 1 },
        { tag: INFOODS.Cholesterol, label: ll.editFood.nutrients.cholesterol(), bold: true },
        { tag: 'NA_mg', label: ll.editFood.nutrients.sodium(), bold: true },
        { tag: INFOODS.CarbTot, label: ll.editFood.nutrients.totalCarbohydrate(), bold: true },
        { tag: INFOODS.Fiber, label: ll.editFood.nutrients.dietaryFiber(), indent: 1 },
        { tag: 'SUGAR_g', label: ll.editFood.nutrients.totalSugars(), indent: 1 },
        { tag: 'SUGAD_g', label: ll.editFood.nutrients.includesAddedSugars(), indent: 2 },
        { tag: INFOODS.Protein, label: ll.editFood.nutrients.protein(), bold: true },
        { tag: 'VITD_ug', label: ll.editFood.nutrients.vitaminD() },
        { tag: 'CA_mg', label: ll.editFood.nutrients.calcium() },
        { tag: 'FE_mg', label: ll.editFood.nutrients.iron() },
        { tag: 'K_mg', label: ll.editFood.nutrients.potassium() },
      ],
    };
  }

  return {
    style: NutritionLabelStyle.EU,
    entries: [
      { tag: INFOODS.KiloJoules, label: ll.editFood.nutrients.energy() },
      { tag: INFOODS.Fat, label: ll.editFood.nutrients.fat() },
      { tag: 'FASAT_g', label: ll.editFood.nutrients.saturates(), indent: 1 },
      { tag: INFOODS.CarbNet, label: ll.editFood.nutrients.carbohydrate() },
      { tag: 'SUGAR_g', label: ll.editFood.nutrients.sugars(), indent: 1 },
      { tag: INFOODS.Fiber, label: ll.editFood.nutrients.fiber() },
      { tag: INFOODS.Protein, label: ll.editFood.nutrients.protein() },
      { tag: 'NACL_mg', label: ll.editFood.nutrients.salt() },
    ],
  };
}

/**
 * Pairs each label row with the displayed value carrying the same nutrient tag.
 *
 * A row whose nutrient the food has no value for keeps no display nutrient, and shows a
 * dash. Returns a new label; the one passed in is not changed.
 */
export function attachDisplayNutrients(
  order: NutritionLabelOrder,
  displayNutrients: UiDisplayNutrient[],
): NutritionLabelOrder {
  return {
    ...order,
    entries: order.entries.map(entry => ({
      ...entry,
      displayNutrient: displayNutrients.find(d => d.tag === entry.tag),
    })),
  };
}

/**
 * The measure the label calls "a serving": the one the app stored under that name, or
 * failing that whatever measure the food lists first.
 */
export function findServingMeasure(measures: FoodMeasure[] | undefined): FoodMeasure | undefined {
  if (!measures) return undefined;
  return measures.find(m => m.name === LABEL_SERVING_MEASURE_NAME) || measures[0];
}

/**
 * How many grams the nutrient inputs on the form are expressed in.
 *
 * In per-100 g mode that is always 100. In per-serving mode it is the weight of the whole
 * serving measure, even when that measure counts several units at once ("3 cookie, 45 g"
 * gives 45, not 15). The hint above the inputs (`buildNutritionHeaderDescription`) names
 * that whole measure, so the values the user types are for all of it (#6024).
 *
 * This number is also what the save path divides by to convert the form's values back to
 * the per-100 g values that get stored, so it is worth being sure about.
 */
export function computeStandardAmount(
  measures: FoodMeasure[] | undefined,
  mode: MacrosDisplayMode,
): number {
  if (mode !== 'serving') return 100;
  const servingSizeMeasure = findServingMeasure(measures);
  if (servingSizeMeasure && servingSizeMeasure.weight_g) {
    return servingSizeMeasure.weight_g;
  }
  return 100;
}

/**
 * Rewrites the form's nutrient values for a new serving size.
 *
 * Used on the read-only view, where the stored per-100 g values are scaled to whatever
 * amount is being shown. Total carbohydrates are dropped rather than scaled, because that
 * figure is never stored — it is worked out again from net carbohydrates and fiber by
 * `reconcileTotalCarbs` below.
 *
 * CHANGES `edited` IN PLACE and does not return it, because the page's template binds
 * two-way into that object. Handing back a fresh one would silently break those bindings.
 */
export function scaleEditedNutrientsToAmount(
  edited: Nutrients,
  stored: Nutrients,
  amount: number,
): void {
  for (let tag in edited) {
    // skip derived tags (e.g. CarbTot) that don't exist in the stored nutrients
    if (stored[tag] == null) continue;
    edited[tag] = (stored[tag] / 100) * amount;
  }
  delete edited[INFOODS.CarbTot];
}

/**
 * The inverse of `scaleEditedNutrientsToAmount`: turns the form's values back into the
 * per-100 g values that get stored, and writes them into the food's own nutrients.
 *
 * A nutrient the user never filled in is stored as `null`, and the form's copy is a clone of
 * the stored values, so `null` is what this function is handed for it. Dividing that would
 * give `0` — turning "nobody measured this" into "somebody measured zero", which is a worse
 * record than the one we started with. `isNutrientValueEntered` treats `0` as a measurement
 * and `null` as none, so the fabricated zero would then read as real everywhere: the food
 * card would show a confident 0, and `getNutrientConsistency` would see every macro present
 * and no calories, which is one of the shapes it accuses a record for (see
 * `design-docs/food-data-quality.md`).
 *
 * Clearing a nutrient in the form is the same case from the other side. The measure input
 * emits `undefined` for an emptied field, which divides to `NaN`, and that only became a
 * stored `null` because `JSON.stringify` writes `NaN` that way on the trip to the database.
 * So both are handled by one rule: a value that is not a measurement is stored as `null`,
 * and everything else is converted.
 *
 * CHANGES `stored` IN PLACE, matching `scaleEditedNutrientsToAmount` above — the page holds
 * the food whose nutrients these are and goes on using it after this returns.
 */
export function storeEditedNutrientsAsPer100g(
  stored: Nutrients,
  edited: Nutrients,
  amount: number,
): void {
  for (let tag in edited) {
    const value = edited[tag];
    stored[tag] = isNutrientValueEntered(value) ? (value / amount) * 100 : null;
  }
}

/**
 * Keeps total carbohydrates and net carbohydrates telling the same story.
 *
 * The two label formats disagree about what "carbohydrates" means. A US label shows TOTAL
 * carbohydrates, which include fiber. The app stores NET carbohydrates, which do not, and
 * an EU label shows those. So whichever of the two the user typed, the other one has to be
 * worked out from it, and the answer depends on which of them the user has touched — which
 * is what the remembered original total is for.
 *
 * The first branch runs while no total has been remembered yet, and keeps running until
 * some value is valid. If a total arrived (the user typed into a US label, or a food was
 * loaded carrying one), net carbohydrates are derived from it by taking fiber back out.
 * Otherwise the total is built from net carbohydrates plus fiber.
 *
 * The second branch runs afterwards, and reacts only to the user editing the total: when it
 * has moved away from the remembered value and is larger than net carbohydrates, net
 * carbohydrates follow it down by the fiber. The "larger than" test is what stops a
 * half-typed number — a total momentarily smaller than the fiber it contains — from
 * rewriting a good value.
 *
 * Returns what to write rather than writing it, so the caller can assign onto the object
 * the template is bound to.
 */
export function reconcileTotalCarbs(
  nutrients: Nutrients,
  totalCarbsOrg_g: number | undefined,
): TotalCarbsReconciliation {
  // while no total has been remembered yet, work out whichever of the two figures is missing
  if (totalCarbsOrg_g == undefined) {
    const carbTot = nutrients[INFOODS.CarbTot];
    const carbNet = nutrients[INFOODS.CarbNet];
    const fiber = nutrients[INFOODS.Fiber];
    // if CarbTot already set (US label user input), derive CarbNet from it
    if (carbTot != undefined) {
      return {
        carbNet: carbNet == undefined ? Math.max(carbTot - (fiber || 0), 0) : undefined,
        totalCarbsOrg_g: carbTot,
      };
    }
    // otherwise compute CarbTot from CarbNet + Fiber (EU label / loading existing food)
    if (carbNet != undefined || fiber != undefined) {
      const totalCarbs = (carbNet || 0) + (fiber || 0);
      return { carbTot: totalCarbs, totalCarbsOrg_g: totalCarbs };
    }
    // nothing valid yet -- ask again next time
    return { totalCarbsOrg_g: undefined };
  }

  // afterwards, follow the user editing the total
  const totalCarbs = nutrients[INFOODS.CarbTot] || 0;
  const netCarbs = nutrients[INFOODS.CarbNet] || 0;
  if (nutrients[INFOODS.CarbTot] && totalCarbs != totalCarbsOrg_g && totalCarbs > netCarbs) {
    const fiber = nutrients[INFOODS.Fiber] || 0;
    return { carbNet: Math.max(totalCarbs - fiber, 0), totalCarbsOrg_g: totalCarbs };
  }
  return { totalCarbsOrg_g };
}

/**
 * Fills in the display-only total carbohydrates row.
 *
 * Total carbohydrates are never stored on a food, so on the read-only Nutrition Facts table
 * that row would otherwise be empty. Work it out from net carbohydrates plus fiber, but
 * only when at least one of those two is actually known — a row reading zero because the
 * food has no carbohydrate data at all would be a claim nobody made.
 *
 * CHANGES the rows it is given, because the caller holds those same objects.
 */
export function synthesizeCarbTotDisplay(displayNutrients: UiDisplayNutrient[]): void {
  const carbTot = displayNutrients.find(d => d.tag === INFOODS.CarbTot);
  if (carbTot && carbTot.value == null) {
    const carbNet = displayNutrients.find(d => d.tag === INFOODS.CarbNet);
    const fiber = displayNutrients.find(d => d.tag === INFOODS.Fiber);
    const totalCarbs = (carbNet?.value || 0) + (fiber?.value || 0);
    if (carbNet?.value != null || fiber?.value != null) {
      carbTot.value = totalCarbs;
      carbTot.valueShort = totalCarbs.toFixed(0);
      carbTot.valueUnit = carbNet?.valueUnit || fiber?.valueUnit || 'g';
    }
  }
}

/**
 * The serving sizes listed under the label, as text.
 *
 * A recipe has one: the whole prepared dish, whose amount is how many servings it yields.
 * Older recipes stored before that measure existed default to one serving. A plain food
 * lists the sizes the user gave it, leaving out the app's own US-label serving entry.
 */
export function buildServingSizeTexts(
  food: Food | Recipe,
  locale: LocaleSettings,
  ll: TranslationFunctions = LL,
): string[] {
  if (isRecipe(food)) {
    const storedPrepared = food.measures?.find(m => m.name === RECIPE_SERVINGS_MEASURE_NAME);
    const preparedMeasure: FoodMeasure = {
      name: RECIPE_SERVINGS_MEASURE_NAME,
      weight_g: food.totalWeight_g || 100,
      amount: storedPrepared?.amount ?? 1,
    };
    return [formatServingSize(preparedMeasure, locale, { useFullName: true }, ll)];
  }

  if (!food.measures || food.measures.length === 0) {
    return ['100 g'];
  }
  return food.measures
    .filter(m => m.name !== LABEL_SERVING_MEASURE_NAME)
    .map(m => formatServingSize(m, locale, { useFullName: true }, ll));
}

/**
 * What the per-serving side of the toggle is called: the measure's own name ("1 cup"), or
 * for a recipe the weight of the whole dish.
 */
export function buildServingSegmentLabel(
  food: Food | Recipe,
  ll: TranslationFunctions = LL,
): string {
  // if recipe, show totalWeight_g as the serving label
  if (isRecipe(food)) {
    return `${food.totalWeight_g || 100}g`;
  }

  if (!food.measures || food.measures.length === 0) {
    return ll.editFood.labels.serving();
  }
  const servingMeasure = findServingMeasure(food.measures);
  if (servingMeasure) {
    // show measure name (e.g., "1 cup", "serving") instead of grams
    const amount = servingMeasure.amount || 1;
    const measureName = translateMeasureName(servingMeasure.name, ll);
    if (amount === 1) {
      return measureName;
    }
    return `${amount} ${measureName}`;
  }
  return ll.editFood.labels.serving();
}

/** What the toggle's currently chosen side is called. */
export function buildMacrosPerLabel(
  food: Food | Recipe,
  mode: MacrosDisplayMode,
  ll: TranslationFunctions = LL,
): string {
  if (mode === 'serving') {
    return buildServingSegmentLabel(food, ll);
  }
  return '100g';
}

/** What the label's header line above the nutrient rows says these numbers are per. */
export interface NutritionHeaderInputs {
  food: Food | Recipe;
  labelStyle: NutritionLabelStyle;
  mode: MacrosDisplayMode;
  /** how many grams the shown values are expressed in; see computeStandardAmount */
  standardAmount: number;
  locale: LocaleSettings;
}

/**
 * The header line above the nutrient rows.
 *
 * The wording follows the label style and the value follows the toggle, so the two vary
 * independently: an EU label says "Per 100 g" or "Per portion of 45 g", while a US label
 * names the serving measure with its weight in brackets, or falls back to the bare weight
 * when the food has no measures.
 */
export function buildNutritionHeaderDescription(
  inputs: NutritionHeaderInputs,
  ll: TranslationFunctions = LL,
): string {
  const { food, labelStyle, mode, standardAmount, locale } = inputs;
  // get the weight to display based on the toggle
  const displayWeight = mode === '100g' ? 100 : standardAmount;

  // EU Label: "Per Xg" or "Per measure of Xg" format
  if (labelStyle !== NutritionLabelStyle.US) {
    const per = ll.editFood.labels.valuesPer();
    const weight = formatMeasure(displayWeight, 'foodweight_g', locale, 'g');
    // if showing per-serving, say "Per portion of Xg"
    if (mode === 'serving') {
      return ll.editFood.labels.perPortionOfWeight({ per, portion: ll.editFood.labels.portion(), weight });
    }
    return `${per} ${weight}`;
  }

  // US Label: show serving info or just weight
  if (mode === 'serving') {
    // serving mode - show "measure name (weight)" with parenthesis for US style
    const servingMeasure = findServingMeasure(food.measures);
    if (servingMeasure) {
      const amount = servingMeasure.amount ?? 1;
      const measureName = translateMeasureName(servingMeasure.name, ll);
      const name = amount === 1 ? measureName : `${amount} ${measureName}`;
      const weight = formatMeasure(servingMeasure.weight_g, 'foodweight_g', locale, 'g');
      return `${name} (${weight})`;
    }
  }
  // 100g mode or no measures - just show weight
  return formatMeasure(displayWeight, 'foodweight_g', locale, 'g');
}

/** One nutrient row's value and unit, or a dash when the food has no value for it. */
export function formatNutrientDisplayValue(entry: NutritionLabelEntry): string {
  if (!entry.displayNutrient) {
    return '-';
  }
  const value = entry.displayNutrient.valueShort;
  const unit = entry.displayNutrient.valueUnit;
  return `${value} ${unit}`;
}

/**
 * The energy row, which shows both units at once because the two label formats lead with
 * different ones.
 */
export function formatEnergyDisplayValue(
  displayNutrients: UiDisplayNutrient[],
  locale: LocaleSettings,
): string {
  const kcal = displayNutrients.find(n => n.tag === 'ENER$_kcal');
  if (kcal && kcal.value !== undefined) {
    // use formatMeasure to format both kJ and kcal consistently with cs-input-measure
    const kJText = formatMeasure(kcal.value, 'nutrient_kJ', locale, 'kJ');
    const kcalText = formatMeasure(kcal.value, 'nutrient_kcal', locale, 'kcal');
    return `${kJText} / ${kcalText}`;
  }
  return '-';
}

/**
 * The nutrients shown under "additional nutrients": everything the food has a value for
 * that the main label does not already list.
 */
export function selectOptionalNutrients(
  displayNutrients: UiDisplayNutrient[] | undefined,
  labelOrder: NutritionLabelOrder,
): UiDisplayNutrient[] {
  if (!displayNutrients) return [];
  // filter out nutrients that are already in the main label
  const mainTags = labelOrder.entries.map(e => e.tag);
  return displayNutrients.filter(d => !mainTags.includes(d.tag) && d.valueShort !== '-');
}
